<template>
  <div id="alert_stream_card" class="h-full self-start">
    <div
      v-if="isConfigured && !isEditing"
      id="alerts_rendering_section"
      class="max-h-[600px] overflow-auto"
    >
      <AlertStreamSection
        :card-name="section.config.card_name"
        :priority-filter="section.config.priority_filter"
        :owner-id="computedOwnerId"
        :alert-agent-id="section.config.alert_agent_id"
        :status-filter="section.config.status_filter"
        :disable-load-more="isCustomerSuccessSummary"
        class="w-full"
      />
    </div>

    <div
      v-else
      id="configuration_section"
      class="flex flex-col justify-between h-full gap-6"
    >
      <div class="flex flex-col gap-6">
        <div id="card_name" class="flex flex-col gap-[6px]">
          <span class="text-[13px] secondary_color">Card Name</span>
          <a-input
            v-model:value="cardName"
            class="input_medium"
            placeholder="Enter a card name"
          />
        </div>

        <div id="priority_filter" class="flex flex-col gap-[6px]">
          <span class="text-[13px] secondary_color">Filter by Priority (Optional)</span>
          <a-select
            v-model:value="selectedPriority"
            class="w-full"
            placeholder="All priorities"
            allow-clear
            :options="priorityOptions"
          >
            <template #suffixIcon>
              <ThemeImage
                :icon="imageNames.BOTTOM_ARROW"
                alt="bottom_arrow"
                class="size-4"
              />
            </template>
          </a-select>
        </div>

        <div id="owner_filter" class="flex flex-col gap-[6px]">
          <span class="text-[13px] secondary_color">Filter by Owner (Optional)</span>
          <a-select
            v-model:value="selectedOwnerId"
            class="w-full"
            placeholder="All owners"
            allow-clear
            show-search
            :options="ownerOptions"
            :field-names="{ label: 'name', value: 'id' }"
            :filter-option="filterOwnerOption"
          >
            <template #suffixIcon>
              <ThemeImage
                :icon="imageNames.BOTTOM_ARROW"
                alt="bottom_arrow"
                class="size-4"
              />
            </template>
          </a-select>
        </div>

        <div id="alert_agent_filter" class="flex flex-col gap-[6px]">
          <span class="text-[13px] secondary_color">Filter by Alert Agent (Optional)</span>
          <a-select
            v-model:value="selectedAlertAgentId"
            class="w-full"
            placeholder="All alert agents"
            allow-clear
            :options="alertAgentOptions"
            :field-names="{ label: 'name', value: 'id' }"
          >
            <template #suffixIcon>
              <ThemeImage
                :icon="imageNames.BOTTOM_ARROW"
                alt="bottom_arrow"
                class="size-4"
              />
            </template>
          </a-select>
        </div>

        <div id="status_filter" class="flex flex-col gap-[6px]">
          <span class="text-[13px] secondary_color">Filter by Status (Optional)</span>
          <a-select
            v-model:value="selectedStatus"
            class="w-full"
            placeholder="All statuses"
            allow-clear
            :options="statusOptions"
          >
            <template #suffixIcon>
              <ThemeImage
                :icon="imageNames.BOTTOM_ARROW"
                alt="bottom_arrow"
                class="size-4"
              />
            </template>
          </a-select>
        </div>
      </div>

      <a-button
        class="primary_medium w-fit self-end"
        :loading="saving"
        :disabled="!cardName"
        @click="handleSaveChanges"
      >
        {{ saving ? "Saving.." : "Save changes" }}
      </a-button>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import store from "@/src/store/index.js";
import DashboardService from "@/src/api/DashboardService.js";
import AlertService from "@/src/api/AlertService.js";
import ThemeImage from "@/src/components/ThemeImage.vue";
import AlertStreamSection from "@/src/components/dashboard_components/sections/AlertStreamSection.vue";
import { priorityOptionsForCard, statusOptionsForCard } from "@/src/utils/alertUtils.js";

const props = defineProps({
  source: { type: String, required: true },
  section: { type: Object, required: true },
  rowIndex: { type: Number, required: true },
  sectionIndex: { type: Number, required: true },
  globalUserId: { type: Number, default: null },
});

const saving = ref(false);
const cardName = ref(props.section.config?.card_name || "");
const selectedPriority = ref(props.section.config?.priority_filter || null);
const selectedOwnerId = ref(props.section.config?.owner_id || null);
const selectedAlertAgentId = ref(props.section.config?.alert_agent_id || null);
const selectedStatus = ref(props.section.config?.status_filter || null);

const priorityOptions = priorityOptionsForCard;
const statusOptions = statusOptionsForCard;

const alertAgentOptions = ref([]);

const users = computed(() => store.getters["users/users"] || []);

const ownerOptions = computed(() => {
  const onlyMeOption = { 
    id: -1, 
    name: "Only Me"
  };
  
  const userOptions = users.value.map((user) => ({
    id: user.id,
    name: user.full_name || user.email || `User ${user.id}`,
  }));
  
  return [onlyMeOption, ...userOptions];
});

const filterOwnerOption = (input, option) => {
  const name = option.name || "";
  return name.toLowerCase().includes(input.toLowerCase());
};

const dashboardDetails = computed(
  () => store.getters["dashboard/dashboardDetails"],
);

const isEditing = computed(() =>
  store.getters["dashboard/isSectionEditing"](props.rowIndex, props.sectionIndex),
);

const isConfigured = computed(() => {
  return !!props.section.config?.card_name;
});

const computedOwnerId = computed(() => {
  if (props.globalUserId !== null && props.globalUserId !== undefined) {
    return props.globalUserId;
  }
  
  const ownerId = props.section.config?.owner_id;
  if (ownerId === -1) {
    const userId = localStorage.getItem("userId");
    return userId ? parseInt(userId) : null;
  }
  return ownerId;
});

const isCustomerSuccessSummary = computed(() => {
  return props.source === "customer_success_component";
});

const handleSaveChanges = async () => {
  try {
    saving.value = true;

    if (props.rowIndex !== null && props.sectionIndex !== null) {
      await store.dispatch("dashboard/updateSectionConfig", {
        rowIndex: props.rowIndex,
        sectionIndex: props.sectionIndex,
        config: {
          card_name: cardName.value,
          priority_filter: selectedPriority.value,
          owner_id: selectedOwnerId.value,
          alert_agent_id: selectedAlertAgentId.value,
          status_filter: selectedStatus.value,
        },
      });

      store.dispatch("dashboard/clearSectionEditing", {
        rowIndex: props.rowIndex,
        sectionIndex: props.sectionIndex,
      });

      const updatedLayout = store.getters["dashboard/dashboardLayout"];

      const payload = {
        name: dashboardDetails.value?.name,
        layout: updatedLayout,
      };

      const response = await DashboardService.builder.updateDashboard(
        dashboardDetails.value?.id,
        payload,
      );

      if (response?.success) {
        await store.dispatch("dashboard/setDashboardDetails", response.data);
      }
    }
  } catch (error) {
    console.error("An error occurred while saving changes: ", error);
  } finally {
    saving.value = false;
  }
};

const fetchUsers = async () => {
  const storeUsers = store.getters["users/users"];
  if (!storeUsers || storeUsers.length === 0) {
    await store.dispatch("users/fetchUsersAndTeams");
  }
};

const fetchAlertAgents = async () => {
  try {
    const response = await AlertService.listAlertAgents(1, 100, null, null);
    if (response?.data?.alert_agents) {
      alertAgentOptions.value = response.data.alert_agents.map(agent => ({
        id: agent.id,
        name: agent.agent_name || agent.alert_agent_name || `Agent ${agent.id}`,
      }));
    }
  } catch (error) {
    console.error("Failed to fetch alert agents:", error);
  }
};

watch(
  () => props.section.config,
  (newConfig) => {
    cardName.value = newConfig?.card_name || "";
    selectedPriority.value = newConfig?.priority_filter || null;
    selectedOwnerId.value = newConfig?.owner_id || null;
    selectedAlertAgentId.value = newConfig?.alert_agent_id || null;
    selectedStatus.value = newConfig?.status_filter || null;
  },
  { deep: true, immediate: true },
);

onMounted(() => {
  fetchUsers();
  fetchAlertAgents();
});
</script>
